import { createElement, getUiRoot, setText } from './dom';
import { setLabel } from './i18n';
import { createIcon } from './icons';
import type { Account, AccountOutcome } from '../account/account';
import type { AccountField, AccountProblem, AccountState } from '../account/allauth';

type View = 'signIn' | 'signUp' | 'verify' | 'signedIn';

const TITLES: Readonly<Record<View, string>> = {
  signIn: 'Sign in',
  signUp: 'Create an account',
  verify: 'Check your email',
  signedIn: 'Account',
};

const RESET_PATH = '/accounts/password/reset/';

export interface AccountDialog {
  open(view?: 'signIn' | 'signUp'): void;
  readonly isOpen: boolean;
  dispose(): void;
}

function field(
  form: HTMLElement,
  name: AccountField,
  label: string,
  type: string,
  autocomplete: AutoFill
) {
  const row = createElement('div', 'account-field', form);
  const caption = createElement('label', '', row);
  caption.htmlFor = `account-${name}`;
  setText(caption, label);
  const input = createElement('input', 'account-input', row);
  input.id = `account-${name}`;
  input.name = name;
  input.type = type;
  input.autocomplete = autocomplete;
  input.required = true;
  const error = createElement('p', 'account-error', row);
  error.id = `account-${name}-error`;
  error.hidden = true;
  input.setAttribute('aria-describedby', error.id);
  return { row, input, error };
}

function header(dialog: HTMLElement) {
  const row = createElement('div', 'gateway-panel-header', dialog);
  const heading = createElement('h2', '', row);
  heading.id = 'account-heading';
  const close = createElement('button', 'panel-close', row);
  close.type = 'button';
  setLabel(close, 'aria-label', 'Close');
  setLabel(close, 'title', 'Close');
  const icon = createIcon('close', 18);
  icon.setAttribute('aria-hidden', 'true');
  close.append(icon);
  return { heading, close };
}

function entry(dialog: HTMLElement) {
  const form = createElement('form', 'account-form', dialog);
  form.noValidate = true;
  const sent = createElement('p', 'account-sent', form);
  const email = field(form, 'email', 'Email address', 'email', 'email');
  const password = field(form, 'password', 'Password', 'password', 'current-password');
  const code = field(form, 'code', 'Code from the email', 'text', 'one-time-code');
  code.input.autocapitalize = 'characters';
  code.input.spellcheck = false;
  const problem = createElement('p', 'account-error account-problem', form);
  problem.setAttribute('role', 'alert');
  problem.hidden = true;
  const notice = createElement('p', 'account-notice', form);
  notice.setAttribute('role', 'status');
  const submit = createElement('button', 'account-submit', form);
  submit.type = 'submit';
  return { form, sent, email, password, code, problem, notice, submit };
}

function links(dialog: HTMLElement) {
  const others = createElement('div', 'account-others', dialog);
  const swap = createElement('button', 'account-link', others);
  swap.type = 'button';
  const resend = createElement('button', 'account-link', others);
  resend.type = 'button';
  setText(resend, 'Send a new code');
  const reset = createElement('a', 'account-link', others);
  reset.href = RESET_PATH;
  reset.target = '_blank';
  reset.rel = 'noopener';
  setText(reset, 'Forgot your password?');
  return { others, swap, resend, reset };
}

function signedInPart(dialog: HTMLElement) {
  const signedIn = createElement('div', 'account-signed-in', dialog);
  const who = createElement('p', 'account-who', signedIn);
  setText(createElement('span', '', who), 'Signed in as');
  const address = createElement('strong', 'account-address', who);
  const signOut = createElement('button', 'account-sign-out', signedIn);
  signOut.type = 'button';
  setText(signOut, 'Sign out');
  const signOutProblem = createElement('p', 'account-error', signedIn);
  signOutProblem.setAttribute('role', 'alert');
  signOutProblem.hidden = true;
  return { signedIn, address, signOut, signOutProblem };
}

function elements() {
  const dialog = createElement('dialog', 'gateway-panel account-panel', getUiRoot());
  dialog.id = 'account-dialog';
  dialog.setAttribute('aria-labelledby', 'account-heading');
  return {
    dialog,
    ...header(dialog),
    ...entry(dialog),
    ...links(dialog),
    ...signedInPart(dialog),
  };
}

/** Signing in, creating an account and confirming its email address by the code the server
 *  mails, without leaving the city. Errors stay next to the field or action that failed. */
class Dialog implements AccountDialog {
  private readonly ui = elements();
  private view: View = 'signIn';
  private busy = false;
  private readonly stop: () => void;

  constructor(
    private readonly account: Account,
    private readonly changed: (open: boolean) => void
  ) {
    const { ui } = this;
    ui.close.addEventListener('click', () => {
      this.hide();
    });
    ui.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.hide();
    });
    ui.dialog.addEventListener('close', () => {
      this.changed(false);
    });
    ui.dialog.addEventListener('keydown', (event) => {
      event.stopPropagation();
    });
    ui.form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submit();
    });
    ui.swap.addEventListener('click', () => {
      this.show(this.view === 'signIn' ? 'signUp' : 'signIn');
      ui.email.input.focus();
    });
    ui.resend.addEventListener('click', () => {
      void this.run(ui.resend, () => this.account.resend(), 'A new code is on its way.');
    });
    ui.signOut.addEventListener('click', () => {
      void this.run(ui.signOut, () => this.account.signOut());
    });
    this.stop = account.onChange((state) => {
      this.follow(state);
    });
    this.follow(account.state);
  }

  get isOpen(): boolean {
    return this.ui.dialog.open;
  }

  open(view: 'signIn' | 'signUp' = 'signIn'): void {
    const state = this.account.state;
    this.show(
      state.kind === 'signedIn' ? 'signedIn' : state.kind === 'verifying' ? 'verify' : view
    );
    if (!this.ui.dialog.open) {
      if (document.pointerLockElement) document.exitPointerLock();
      this.ui.dialog.showModal();
      this.changed(true);
    }
    this.focusFirst();
  }

  dispose(): void {
    this.stop();
    this.hide();
    this.ui.dialog.remove();
  }

  private hide(): void {
    if (this.ui.dialog.open) this.ui.dialog.close();
  }

  private follow(state: AccountState): void {
    if (state.kind === 'verifying') this.show('verify');
    else if (state.kind === 'signedIn') this.show('signedIn');
    else if (this.view === 'verify' || this.view === 'signedIn') this.show('signIn');
  }

  private show(view: View): void {
    const { ui } = this;
    const state = this.account.state;
    this.view = view;
    this.clear();
    setText(ui.heading, TITLES[view]);
    const form = view !== 'signedIn';
    const entering = view === 'signIn' || view === 'signUp';
    ui.form.hidden = !form;
    ui.others.hidden = !form;
    ui.signedIn.hidden = form;
    ui.email.row.hidden = !entering;
    ui.password.row.hidden = !entering;
    ui.code.row.hidden = view !== 'verify';
    ui.email.input.required = ui.password.input.required = entering;
    ui.code.input.required = view === 'verify';
    ui.password.input.autocomplete = view === 'signUp' ? 'new-password' : 'current-password';
    ui.sent.hidden = view !== 'verify';
    setText(ui.sent, 'We sent a code to your email address. Enter it here to finish.');
    setText(
      ui.submit,
      view === 'signUp' ? 'Create account' : view === 'verify' ? 'Confirm' : 'Sign in'
    );
    ui.swap.hidden = !entering;
    setText(
      ui.swap,
      view === 'signIn' ? 'No account yet? Create one' : 'Already have an account? Sign in'
    );
    ui.resend.hidden = view !== 'verify';
    ui.reset.hidden = view !== 'signIn';
    ui.address.textContent = state.kind === 'signedIn' ? state.email : '';
  }

  private focusFirst(): void {
    const { ui } = this;
    const target =
      this.view === 'verify'
        ? ui.code.input
        : this.view === 'signedIn'
          ? ui.signOut
          : ui.email.input.value
            ? ui.password.input
            : ui.email.input;
    target.focus();
  }

  private clear(): void {
    const { ui } = this;
    for (const one of [ui.email, ui.password, ui.code]) {
      one.error.replaceChildren();
      one.error.hidden = true;
      one.input.removeAttribute('aria-invalid');
    }
    for (const general of [ui.problem, ui.signOutProblem]) {
      general.replaceChildren();
      general.hidden = true;
    }
    ui.notice.textContent = '';
  }

  private report(problems: readonly AccountProblem[], general: HTMLElement): void {
    const fields = { email: this.ui.email, password: this.ui.password, code: this.ui.code };
    let first: HTMLInputElement | null = null;
    for (const problem of problems) {
      const target = problem.field ? fields[problem.field] : null;
      const shown = target && !target.row.hidden ? target.error : general;
      setText(createElement('span', 'account-error-line', shown), problem.message);
      shown.hidden = false;
      if (target && shown === target.error) {
        target.input.setAttribute('aria-invalid', 'true');
        first ??= target.input;
      }
    }
    first?.focus();
  }

  private missing(): readonly AccountProblem[] {
    const { ui } = this;
    return [ui.email, ui.password, ui.code]
      .filter((one) => !one.row.hidden && one.input.value.trim() === '')
      .map((one) => ({ field: one.input.name as AccountField, message: 'Fill in this field.' }));
  }

  private async submit(): Promise<void> {
    const { ui } = this;
    const empty = this.missing();
    if (empty.length > 0) {
      this.clear();
      this.report(empty, ui.problem);
      return;
    }
    const email = ui.email.input.value.trim();
    const password = ui.password.input.value;
    const view = this.view;
    const done = await this.run(ui.submit, () =>
      view === 'signUp'
        ? this.account.signUp(email, password)
        : view === 'verify'
          ? this.account.verify(ui.code.input.value.trim().toUpperCase())
          : this.account.signIn(email, password)
    );
    if (!done) return;
    ui.password.input.value = '';
    ui.code.input.value = '';
    if (this.account.state.kind === 'signedIn') this.hide();
    else this.focusFirst();
  }

  private async run(
    control: HTMLButtonElement,
    action: () => Promise<AccountOutcome>,
    success?: string
  ): Promise<boolean> {
    if (this.busy) return false;
    this.busy = true;
    control.disabled = true;
    control.setAttribute('aria-busy', 'true');
    this.clear();
    try {
      const outcome = await action();
      if (!outcome.ok) {
        this.report(
          outcome.problems,
          control === this.ui.signOut ? this.ui.signOutProblem : this.ui.problem
        );
        return false;
      }
      if (success) setText(this.ui.notice, success);
      return true;
    } finally {
      this.busy = false;
      control.disabled = false;
      control.removeAttribute('aria-busy');
    }
  }
}

export function createAccountDialog(
  account: Account,
  changed: (open: boolean) => void
): AccountDialog {
  return new Dialog(account, changed);
}
